import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

// Helpers
import { completeColumns, getTagColor } from 'Core/data/Helpers';

// Components
import { Grid } from '@mui/material';

// Custom Components
import Preferences from "Core/containers/Preferences";
import { DynamicTooltip, IconButton, MenuComponent, Pill, SearchInput, TableButtons, TableView, useTablePagination, Tooltip, Icons } from '@gpstrackit/gtc-react-controls';
import { NotificationManager } from "../../../../components/Notifications";

//Views
// Redux Actions
import { reset, searchDriverPerformanceCases } from './redux/slice';
import { clearRedux, savePreference, sendGenericExport } from 'Redux/actions';

// Helpers
import { getInitialRange, removeDiacritics } from '../../.globals/helpers';
//Modals
import AdvancedFilters from './modals/advancedFilters';

// Styles
import './style.scss';
import { GetFormatForMoment , useRealtimeTags} from 'Core/hooks';
import { openModalAdvancedFilters } from "Modules/driverPerformance/views/cases/modals/advancedFilters/redux/slice";
import { openModalCreatePanel } from "Modules/driverPerformance/views/cases/modals/createPanel/redux/slice";
import { openModalEditCoachPanel, resetEditCoachPanel } from "Modules/driverPerformance/views/cases/modals/editCoachPanel/redux/slice";
import DriverInfo from "Modules/driverPerformance/views/events/modals/driverInfo";
import { Timezone } from "Components/uiControls";
import { TagsGroup } from "Components/Tag";
import CreatePanel from "Modules/driverPerformance/views/cases/modals/createPanel";
import colors from "Assets/sass/core/vars.scss";

// Constants
const KEY_PREFERENCE = "DriverPerformanceViewCases";
const DEFAULT_SORT = { field: "createdOn", order: "DESC" };

// Initial State
const INITIAL_STATE = {
    rangeDate: {
        start: moment.utc().startOf('month').toDate(),
        end: moment.utc().endOf('day').toDate()
    },
    timeFrame: 'thisMonth',
    tags: [],
    coaches: [],
    status: [],
    drivers: []
}

// Helper function to parse URL parameters and convert them to filter format
const parseUrlToFilters = (urlParams, statusOptions, drivers, riskEventsByDriver, coaches) => {
    const filters = { ...INITIAL_STATE };

    // Parse range parameter
    if (urlParams.get('range')) {
        filters.timeFrame = urlParams.get('range');
        let rangeDate = getInitialRange(filters.timeFrame, null, null, true)
        if (rangeDate) {
            filters.rangeDate = {
                start: rangeDate.start,
                end: rangeDate.end
            };
        }
    }

    if (urlParams.get('startDate') && urlParams.get('endDate')) {
        filters.timeFrame = 'custom';
        let rangeDate = getInitialRange('custom', urlParams.get('startDate'), urlParams.get('endDate'), true)
        filters.rangeDate = {
            start: rangeDate.start,
            end: rangeDate.end
        };
    }

    if (urlParams.get('statusCases')) {
        const statusCasesParam = urlParams.get('statusCases');
        // Dividir por comas y limpiar espacios
        const statusArray = statusCasesParam.split(',').map(s => s.trim());

        // Buscar cada status en statusOptions
        const foundStatuses = statusArray
            .map(statusId => statusOptions.find(status => status.id === statusId))
            .filter(Boolean); // Filtrar los que no se encontraron (undefined)

        if (foundStatuses.length > 0) {
            filters.status = foundStatuses;
        }
    }
    if (urlParams.get('caseNumbers')) {
        const caseNumbersParam = urlParams.get('caseNumbers');
        // Convert comma-separated string to array
        const caseNumbersArray = caseNumbersParam.split(',').map(caseNum => caseNum.trim());

        filters.casesNumber = caseNumbersArray;
    }

    if (urlParams.get('casesDriverIds')) {
        const driversParam = urlParams.get('casesDriverIds');
        const driverIdsArray = driversParam.split(',').map(id => id.trim());
        let catalogDrivers = [];

        // First, search in the drivers catalog
        driverIdsArray.forEach(driverId => {
            const foundDriver = drivers?.find(driver =>
                String(driver.driverId) === String(driverId) ||
                String(driver.id) === String(driverId)
            );

            if (foundDriver) {
                catalogDrivers.push(foundDriver);
            } else {
                // If not found in drivers, search in riskEventsByDriver
                if (riskEventsByDriver?.drivers) {
                    const riskDriver = riskEventsByDriver.drivers.find(driver =>
                        String(driver.driverId) === String(driverId) ||
                        String(driver.id) === String(driverId)
                    );

                    if (riskDriver) {
                        catalogDrivers.push(riskDriver);
                    }
                }
            }
        });

        if (catalogDrivers.length > 0) {
            filters.drivers = catalogDrivers;
        }
    }

    if (urlParams.get('coachId')) {
        const coachIdParam = urlParams.get('coachId');
        if (coaches && coaches.length > 0) {
            const foundCoach = coaches.find(coach => String(coach.id) === String(coachIdParam));
            filters.coaches = foundCoach ? [foundCoach] : [];
        } else {
            filters.coaches = [{ id: Number(coachIdParam) }];
        }
    }

    return filters;
};
export const DriverPerformanceCasesView = (props) => {
    const { messages, locale } = useIntl();
    const dispatch = useDispatch();
    const casesTableRef = useRef();
    const navigate = useNavigate();

    const [formData, setFormData] = useState(INITIAL_STATE);
    const [preferences, setPreferences] = useState({});
    const [loadPreference, setLoadPreferences] = useState(false);
    const [loadColumns, setLoadColumns] = useState(false);
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [page, setPage] = useState(1);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [sortState, setSortState] = useState(DEFAULT_SORT);
    const [tableLoading, setTableLoading] = useState(false);

    // Redux state
    const { isKph, dateformat, timeZone } = useSelector((state) => state.securityRedux.user);
    const { error, loading, cases, nextToken } = useSelector(state => state.driverPerformanceCasesTabRedux);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const format = GetFormatForMoment(dateformat);
    const { coaches } = useSelector((state) => state.driverPerformanceCasesTabAdvancedFiltersRedux);
    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
    const openModalFilters = useSelector((state) => state.driverPerformanceCasesTabAdvancedFiltersRedux.open);
    const openModalCreate = useSelector((state) => state.driverPerformanceCasesTabCreatePanelRedux.open);
    const drivers = useSelector((state) => state?.catalogsRedux?.drivers);
    const riskEventsByDriver = useSelector(state => state?.dashboardRedux?.gadgetsData?.RiskEventsByDriver || {});



    useEffect(() => {
        if (showCreateModal) {
            dispatch(openModalCreatePanel({ open: !openModalCreate }));
        }
    }, [showCreateModal])

    const statusOptions = [{
        id: 'Pending', label: messages['driver_performance_cases_filters_pending'], color: 'grey',
    }, {
        id: 'Evaluate', label: messages['driver_performance_cases_filters_evaluate'], color: 'orange',
    }, {
        id: 'Completed', label: messages['driver_performance_cases_filters_completed'], color: '#66bb6a',
    }];

    const paginationCases = useTablePagination({
        dispatch,
        action: searchDriverPerformanceCases,
        searchFields: ["openSearch"],
        tableRef: casesTableRef,
        sendGenericExport,
        exportName: "casesTab",
    });

    function handleUserSearchInput(value, updatedParams) {
        if (window.typingTime) {
            clearTimeout(window.typingTime);
        }
        window.typingTime = setTimeout(() => {
            savePreferences([
                {
                    key: "openSearch",
                    value: value
                }
            ])
            paginationCases.refresh(updatedParams);
        }, 1000);
    }

    const applyChanges = (field, value, filters) => {
        if (typeof field === 'object' && field !== null) {
            let updatedParams = {
                _conditions: buildConditions({ ...(field), page: 1 }),
            };
            // Reset the table page
            setPage(1);
            casesTableRef?.current?.setPage(1);
            paginationCases.refresh(updatedParams);
            return;
        }

        let updatedParams = {
            _conditions: buildConditions({ ...filters, page: 1 }),
        };

        if (field === 'openSearch') {

            const cleanValue = value.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ\s\-\_,\.\(\)]/g, '');

            if (!cleanValue || cleanValue.trim().length === 0) {
                if (formData.openSearch && formData.openSearch !== '') {
                    handleUserSearchInput(cleanValue.trim(), {
                        _conditions: buildConditions(formData)
                    });
                }
                setFormData(prev => ({ ...prev, openSearch: cleanValue.trim() }));
                return;
            }

            updatedParams._conditions.push(
                {
                    fields: ['caseNumber', 'driver_name'],
                    terms: [value]
                },
                {
                    page: 1
                })
            setFormData(prev => ({ ...prev, openSearch: value }));
            handleUserSearchInput(value, updatedParams);
        } else if (field !== 'openSearch') {
            paginationCases.refresh(updatedParams);
        }
    }

    // hasFilters function
    const hasFilters = useCallback(() => {
        const excludedKeys = ["rangeDate", 'page', 'openSearch'];
        const defaultValues = {
            timeFrame: "thisMonth",
            tags: [],
            coaches: [],
            status: [],
            drivers: []
        };

        return Object.keys(formData).some(key => {
            if (excludedKeys.includes(key)) {
                return false;
            }

            const currentValue = formData[key];
            const defaultValue = defaultValues[key];

            // For arrays, compare length and content
            if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
                return currentValue.length !== defaultValue.length ||
                    currentValue.some((item, index) => item !== defaultValue[index]);
            }

            // For other types, strict comparison
            return currentValue !== defaultValue;
        });
    }, [formData]);

    const generateExportStyles = () => {
        return {
            0: {
                cellWidth: 60,
                fontSize: 11,
                fontStyle: "bold",
            }
        };
    };

    const savePreferences = (preferencesArray) => {

        const urlSearchParams = new URLSearchParams(location.search);
        const urlParamsArray = Array.from(urlSearchParams.entries());
        const hasUrlParams = urlParamsArray.length > 0;

        // Check if the only URL param is 'tab=cases'
        const onlyTabCasesParam = hasUrlParams &&
            urlParamsArray.length === 1 &&
            urlSearchParams.get('tab') === 'cases';

        const isColumnChanging = preferencesArray.length ? preferencesArray?.some(pref => pref.key === 'columns') : false;

        if (!hasUrlParams || isColumnChanging || onlyTabCasesParam) {
            if (!Array.isArray(preferencesArray)) {
                console.error("Expected an array of preferences");
                return;
            }

            let updatedPreferences = { ...preferences } || {};

            preferencesArray.forEach(({ key, value }) => {
                updatedPreferences[key] = value;
            });

            setPreferences(updatedPreferences);

            dispatch(savePreference({
                entityName: "User",
                entityId: authenticatedId,
                preferenceKey: KEY_PREFERENCE,
                preferenceValue: updatedPreferences
            }));
        }
    };

    let currentColumns = [
        {
            key: 'caseNumber',
            title: messages['driver_performance_cases_caseId'],
            show: true,
            width: 150,
            export: true,
            render: (row) => {
                return (
                    <div className={'performance-cases-caseNumber-menu'}>
                        <a
                            href={`/app/driverPerformance/caseDetail/${row?.id}`}
                            className={'performance-cases-caseNumber-menu-link'}
                            noWrap
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/app/driverPerformance/caseDetail/${row?.id}`)
                            }}>
                            {row?.caseNumber || "N/A"}
                        </a>
                        <MenuComponent
                            icon="more_vert_icon"
                            onClick={() => dispatch(openModalEditCoachPanel({ open: true, dataToCase: row }))}
                            options={[messages['driver_performance_cases_assignCoach']]}
                            variant="primary"
                        />
                    </div>
                )
            },
            exportValue: (row) => {
                return row?.caseNumber || "N/A";
            },
            functionString: 'return row?.caseNumber || "N/A"'
        },
        {
            key: 'status_label',
            title: messages['driver_performance_cases_status'], //name
            show: true,
            sort: true,
            export: true,
            width: 200,
            render: (row) => {
                const item = {
                    ...row?.status,
                    label: messages[`driver_performance_cases_status_${row?.status?.label?.toLowerCase()}`] || row?.status?.label,
                    color: row?.status?.label === 'Expired' ? "black" : row?.status?.label === 'Pending' ? "grey" : row?.status?.label === 'Evaluate' ? "orange" : "#66bb6a"
                }

                return (
                    <div style={{ width: 110 }} className="casesTab-badge-status">
                        <Pill
                            key={row?.id}
                            item={item}
                            onClick={function noRefCheck() { }}
                            variant={'outlined'}
                        />
                    </div>
                )
            },
            exportValue: (row) => {
                return messages[`driver_performance_cases_status_${row?.status?.label?.toLowerCase()}`] || row?.status?.label;
            },
            functionString: `
            const translations = {
                    Pending: '${messages['driver_performance_cases_status_pending'] || 'Pending'}',
                    Evaluate: '${messages['driver_performance_cases_status_evaluate'] || 'Evaluate'}',
                    Completed: '${messages['driver_performance_cases_status_completed'] || 'Completed'}',
                };
            return translations[row?.status?.label] || row?.status?.label`
        },
        {
            key: 'nextStep',
            title: messages['driver_performance_cases_nextStep'], //name
            show: true,
            width: 120,
            export: true,
            render: (row) => {
                return (
                    <div style={{ width: 100 }}>
                        <span>{messages[`driver_performance_cases_step_${row?.status?.nextStep.replace(/\s/g, "")}`] || row?.status?.nextStep}</span>
                    </div>
                )
            },
            exportValue: (row) => {
                return messages[`driver_performance_cases_step_${row?.status?.nextStep.replace(/\s/g, "")}`] || row?.status?.nextStep
            },
            functionString: `
            const translations = {
                'AssigCoach': '${messages['driver_performance_cases_step_AssignCoach'] || 'Assign Coach'}',
                'InitiateCoaching': '${messages['driver_performance_cases_step_InitiateCoaching'] || 'Initiate Coaching'}',
                'Monitor': '${messages['driver_performance_cases_step_Monitor'] || 'Monitor'}',
                'PIOmited': '${messages['driver_performance_cases_step_PIOmited'] || 'PI Omited'}',
                'PISuccessfull': '${messages['driver_performance_cases_step_PISuccessfull'] || 'PI Successfull'}',
                'Judgement': '${messages['driver_performance_cases_step_Judgement'] || 'Judgement'}',
                'PIFailed': '${messages['driver_performance_cases_step_PIFailed'] || 'PI Failed'}',
            };
            return translations[row?.status?.nextStep.replace(/\s/g, "")] || row?.status?.nextStep`
        },
        {
            key: 'driverName',
            title: messages['driver_performance_cases_driver'],
            show: true,
            width: 250,
            export: true,
            functionString: 'return row?.driver?.name || ""',
            exportValue: (row) => {
                return row?.driver?.name;
            },
            render: (row) => {
                const driverKey = `driver-${row?.driverId}`;
                return (
                    <div style={{ width: 250 }}>
                        <DynamicTooltip
                            key={row?.id}
                            trigger={<span style={{
                                fontFamily: 'Outfit, sans-serif',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 400,
                                borderBottom: '1px dashed #595151cc',
                                display: 'inline-block'
                            }}>{row?.driver?.name}</span>}
                            openOn="click"
                            size="medium"
                            position="top"
                        >
                            {(isOpen) => (
                                <DriverInfo
                                    driverKey={driverKey}
                                    openTooltip={isOpen}
                                    driverId={row?.driverId}
                                />
                            )}
                        </DynamicTooltip>
                    </div>
                );
            }
        },
        {
            key: 'coach',
            title: messages['driver_performance_cases_coach'],
            show: true,
            width: 250,
            export: true,
            functionString: 'return (row?.coach?.firstName || "") + " " + (row?.coach?.lastName || "")',
            exportValue: (row) => {
                return `${row?.coach?.firstName || ''} ${row?.coach?.lastName || ''}`;
            },
            render: (row) => {
                return (
                    <div style={{ width: 100 }}>
                        <span>{`${row?.coach?.firstName || ''} ${row?.coach?.lastName || ''}` || ''}</span>
                    </div>
                )
            }
        },
        {
            key: 'createdOn',
            title: messages['driver_performance_cases_created'],
            show: true,
            width: 200,
            sort: true,
            export: true,
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToFormat = moment(date)
                    let formatted = moment(dateToFormat).tz(tz).format(formatDate)
                    return formatted;
                }

                function getAbBvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    switch (abbr) {
                        case '-05':
                            return 'COT';
                        default:
                            return abbr;
                    }
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a"
                let timezoneAbbr = getAbBvTimezone(timeZone)

                return row?.createdOn ? formatDate(row?.createdOn,timeZone,format) + ", " + timezoneAbbr : ''
            `,
            exportValue: (row) => {
                function formatDate(date, tz, formatDate) {
                    let dateToFormat = moment(date)
                    return moment(dateToFormat).tz(tz).format(formatDate);
                }

                function getAbBvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    if (abbr === '-05') {
                        return 'COT'
                    } else {
                        return abbr;
                    }
                }

                let timezoneAbbr = getAbBvTimezone(timeZone)

                return row?.createdOn ? formatDate(row?.createdOn, timeZone, format + " hh:mm a") + ", " + timezoneAbbr : ''
            },
            render: (row) => (
                <Timezone
                    date={row?.createdOn}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        },
        {
            key: 'completedDate',
            title: messages['driver_performance_cases_completed'],
            show: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToFormat = moment(date)
                    let formatted = moment(dateToFormat).tz(tz).format(formatDate)
                    return formatted;
                }

                function getAbBvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    switch (abbr) {
                        case '-05':
                            return 'COT';
                        default:
                            return abbr;
                    }
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a"
                let timezoneAbbr = getAbBvTimezone(timeZone)

                return row?.completedDate ? formatDate(row?.completedDate,timeZone,format) + ", " + timezoneAbbr : ''
            `,
            exportValue: (row) => {
                function formatDate(date, tz, formatDate) {
                    let dateToFormat = moment(date)
                    return moment(dateToFormat).tz(tz).format(formatDate);
                }

                function getAbBvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    if (abbr === '-05') {
                        return 'COT'
                    } else {
                        return abbr;
                    }
                }

                let timezoneAbbr = getAbBvTimezone(timeZone)

                return row?.completedDate ? formatDate(row?.completedDate, timeZone, format + " hh:mm a") + ", " + timezoneAbbr : ''
            },
            render: (row) => (
                row?.completedDate && <Timezone
                    date={row?.completedDate}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        },
        {
            key: 'driverCaseEventsCount',
            title: messages['driver_performance_cases_eventCount'],
            show: true,
            width: 100,
            export: true,
            functionString: `
                return row?.driverCaseEventsCount || 0;
            `,
            exportValue: (row) => {
                return Math.round(row?.driverCaseEventsCount) || 0;
            },
            render: (row) => {
                return (
                    <div style={{ width: 100 }}>
                        <span>{Math.round(row?.driverCaseEventsCount) || 0}</span>
                    </div>
                )
            }
        },
        {
            key: 'tags',
            show: true,
            title: messages['driver_performance_cases_tags'],
            width: 200,
            export: true,
            render: (item) => {
                if (!item?.driver?.tagIds) return "";
                let tagIds = item?.driver?.tagIds?.startsWith("[")
                    ? JSON.parse(item?.driver?.tagIds || "[]")
                    : item?.driver?.tagIds?.split(",");

                let userTags = []
                if (tagIds?.includes("-99") || tagIds?.includes(-99) || tagIds?.length === 0) {
                    userTags = [{ id: -99, label: messages['tagPicker_allTagsAreSelected'], color: colors?.blue_gtc }];
                } else {
                    userTags = tags?.filter(tag => tagIds?.includes(tag?.id));
                }

                return (
                    <div style={{ width: 200 }}>
                        <TagsGroup
                            options={(userTags || []).map(ut => {
                                return {
                                    ...ut,
                                    label: ut?.id == -1 ? messages[ut?.label] : ut?.label
                                }
                            })}
                            nameField="label"
                            colorField="color"
                        />
                    </div>
                )
            },
            exportValue: (item) => {
                let tagIds = item?.driver?.tagIds?.startsWith("[")
                    ? JSON.parse(item?.driver?.tagIds || "[]")
                    : item?.driver?.tagIds?.split(",");

                if (tagIds?.includes("-99") || tagIds?.includes(-99) || tagIds?.length === 0) {
                    return messages['tagPicker_allTagsAreSelected'];
                } else {
                    return item?.driver?.tagNames;
                }
            },
            functionString: `
                let tagIds = row?.driver?.tagIds?.startsWith("[")
                    ? JSON.parse(row?.driver?.tagIds || "[]")
                    : row?.driver?.tagIds?.split(",");

                if (tagIds?.includes("-99") || tagIds?.includes(-99) || tagIds?.length === 0) {
                    return '${messages['tagPicker_allTagsAreSelected'] || 'All Tags'}';
                } else {
                    return row?.driver?.tagNames;
                }
            `
        }
    ]

    useEffect(() => {
        paginationCases.setColumns(currentColumns);
    }, []);

    useEffect(() => {
        if (casesTableRef?.current && !tableLoading) {
            casesTableRef?.current?.setSelectedItems([]);
            setSelectedItems([]);
            casesTableRef?.current?.setSort({ key: "createdOn", order: "desc" });
            casesTableRef?.current?.setPage(1);
            setTableLoading(true)
        }
    }, [casesTableRef?.current])

    useEffect(() => {
        if (error) {
            NotificationManager.error(messages["errorMessage"], messages["error"], 3000);
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "casesTab") {
            NotificationManager.info(messages["exportInProcessBody"], messages["exportInProcessTitle"], 4000);
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName, messages, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);


    useEffect(() => {
        if (preferences?.columns?.length && !loadColumns) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];

            paginationCases.setColumns(finalColumns);
            setPreferences({ ...preferences });
            setLoadColumns(true);
        }
    }, [preferences?.columns, loadColumns])

    useEffect(() => {
        if (paginationCases.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationCases.columns })
            setLoadColumns(false);
        }
    }, [locale, timeZone, dateformat, isKph]);

    // Watch for URL changes
    useEffect(() => {
        const urlSearchParams = new URLSearchParams(location.search);

        // Only consider relevant URL parameters for Cases tab
        const relevantCasesParams = ['range', 'startDate', 'endDate', 'statusCases', 'caseNumbers'];
        const hasRelevantUrlParams = relevantCasesParams.some(param => urlSearchParams.has(param));

        if (hasRelevantUrlParams && loadPreference) {

            const filtersFromUrl = parseUrlToFilters(urlSearchParams, statusOptions, drivers, riskEventsByDriver, coaches);
            const updatedFormData = { ...filtersFromUrl };

            setFormData(updatedFormData);
            const currentConditions = buildConditions(updatedFormData);
            paginationCases.refresh({ _conditions: currentConditions });
            setPage(1);
            casesTableRef?.current?.setPage(1);
        }
    }, [location.search]);

    return (
        <Fragment>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: 8 }}
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            <IconButton
                                tooltip={messages["filters"]}
                                placement="bottom"
                                icon="filter_list"
                                size="small"
                                disabled={loading}
                                onClick={() => {
                                    dispatch(openModalAdvancedFilters({ open: !openModalFilters }));
                                }}
                                className={hasFilters() ? "casesTab-filters-available" : ""}
                            />
                            <IconButton
                                tooltip={messages["add"]}
                                placement="bottom"
                                icon="add_box"
                                style={{ color: "black" }}
                                size="small"
                                disabled={loading}
                                onClick={() => {
                                    dispatch(openModalCreatePanel({ open: !openModalCreate }));
                                }}
                            />
                            <TableButtons
                                {...paginationCases}
                                messages={messages}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    if (selectedItems?.length) {
                                        if (type === "Csv") {
                                            paginationCases?.tableRef.current.exportCSV();
                                        } else if (type === "Pdf") {
                                            paginationCases?.tableRef.current.exportPDF();
                                        }
                                    } else {
                                        let headers = paginationCases.columns.filter(col => col.export && col.show)
                                            .map(col => ({
                                                header: removeDiacritics(col.title),
                                                column: col.key,
                                                functionString: col.functionString || `return row?.${col.key}`
                                            }));

                                        const conditions = buildConditions({ ...formData, page: 1 });
                                        const currentConditions = conditions.filter((item) => !item.page);

                                        dispatch(
                                            sendGenericExport(
                                                "casesTab",
                                                type,
                                                searchDriverPerformanceCases.query,
                                                { conditions: currentConditions, sort: [sortState] },
                                                headers,
                                                {
                                                    unit: 'px',
                                                    size: 'A4',
                                                    orientation: 'landscape',
                                                    fontSize: 10,
                                                },
                                                null,
                                                true,
                                                true,
                                                generateExportStyles()
                                            )
                                        );
                                    }
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences([{ key: "columns", value: columns }])
                                    paginationCases.setColumns(columns)
                                }}
                                columns={paginationCases.columns}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6} sm={4} md={4} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                    <Tooltip
                        placement="bottom"
                        title={messages['driver_performance_driver_timeInfo_message']}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Icons name="help" size="medium" />
                        </span>
                    </Tooltip>
                    <Pill
                        key={'pillTime'}
                        textField={"label"}
                        variant='filled'
                        item={{
                            id: "date",
                            label: `${messages[`driver_performance_events_${formData?.timeFrame}`] || formData?.timeFrame} (${moment(formData?.rangeDate?.start).format(`${GetFormatForMoment(dateformat)} hh:mm a`)} - ${moment(formData?.rangeDate?.end).format(`${GetFormatForMoment(dateformat)} hh:mm a`)})`,
                            color: '#005bf8'
                        }}
                        onClick={() => {
                            dispatch(openModalAdvancedFilters({ open: !openModalFilters }));
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={4} className="text-right">
                    <SearchInput
                        placeholder={messages["driver_performance_cases_searchInput"]}
                        searchText={formData?.openSearch || ""}
                        onSearchText={(value) => applyChanges('openSearch', value, formData)}
                    />
                </Grid>

            </Grid>


            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    if (!loadPreference) {
                        try {
                            // Check for URL parameters first
                            const urlSearchParams = new URLSearchParams(location.search);
                            const urlParamsArray = Array.from(urlSearchParams.entries());
                            const hasUrlParams = urlParamsArray.length > 0;

                            // Check if the only URL param is 'tab=cases'
                            const onlyTabCasesParam = hasUrlParams &&
                                urlParamsArray.length === 1 &&
                                urlSearchParams.get('tab') === 'cases';

                            setShowCreateModal(false)
                            let updatedFormData;
                            if (hasUrlParams && !urlParamsProcessed && !onlyTabCasesParam) {
                                let undated = true
                                // Priority: URL parameters over user preferences
                                if (urlSearchParams.get('showCreateModal')) {
                                    setShowCreateModal(urlSearchParams.get('showCreateModal'))
                                    undated = false
                                }
                                const filtersFromUrl = parseUrlToFilters(urlSearchParams, statusOptions, drivers, riskEventsByDriver, coaches);
                                updatedFormData = { ...filtersFromUrl, undated: undated };
                                setUrlParamsProcessed(true);
                            } else {
                                // Fallback: Use user preferences
                                updatedFormData = {
                                    ...formData, ...['rangeDate', 'tags', 'coaches', 'status', 'drivers'].reduce((acc, key) => {
                                        if (data?.[key]) acc[key] = data[key];
                                        return acc;
                                    }, {})
                                };
                                if (data?.timeFrame) {
                                    const timeFrame = getInitialRange(data.timeFrame, data?.rangeDate?.start, data?.rangeDate?.end, true);
                                    if (timeFrame?.start && timeFrame?.end) {
                                        updatedFormData = {
                                            ...updatedFormData,
                                            rangeDate: timeFrame,
                                            timeFrame: data.timeFrame
                                        }
                                    }
                                }
                            }
                            const currentConditions = buildConditions(updatedFormData);
                            paginationCases.refresh({ _conditions: currentConditions });
                            setFormData(updatedFormData)
                            setPreferences(data)
                            setLoadPreferences(true)
                        } catch (error) {

                        }
                    }
                }}
            >

                <TableView
                    {...paginationCases}
                    keyColumn="caseNumber"
                    data={cases || []}
                    loading={loading}
                    topheight={250}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationCases?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences([{ key: "columns", value: finalsColumns }])
                        paginationCases.setColumns(finalsColumns)
                    }}
                    onChangePage={() => {
                        if (cases?.length) {
                            let newPage = page + 1
                            setPage(newPage);
                            const currentConditions = buildConditions({ ...formData, page: newPage });
                            paginationCases.refresh({ _conditions: currentConditions });
                        }
                    }}
                    onChangeSort={(newSort) => {
                        setSortState({
                            field: newSort?.key,
                            order: newSort?.order.toUpperCase()
                        });
                        const currentConditions = buildConditions({ ...formData, page: 1 });
                        paginationCases.refresh({ _conditions: currentConditions, _sort: newSort, });
                        setPage(1);
                        casesTableRef?.current?.setPage(1);
                    }}
                    infiniteScroll
                    nextToken={nextToken}
                    noMoreItemsMessage={messages?.['driver_performance_noMoreItems']}
                />
            </Preferences>
            <CreatePanel />
            <AdvancedFilters
                tableRef={paginationCases}
                formData={formData}
                setFormData={setFormData}
                initialState={INITIAL_STATE}
                applyChanges={applyChanges}
                hasFilters={hasFilters}
                savePreferences={savePreferences}
                loading={loading}
            />
        </Fragment>
    );
};


const buildConditions = (formData) => {
    const conditions = [];


    if (formData?.rangeDate?.start && formData?.rangeDate?.end) {
        const isCustom = formData?.timeFrame === 'custom';

        const startUTC = isCustom
            ? moment.utc(moment(formData.rangeDate.start).format('YYYY-MM-DD')).startOf('day').toDate()
            : formData.rangeDate.start;

        const endUTC = isCustom
            ? moment.utc(moment(formData.rangeDate.end).format('YYYY-MM-DD')).endOf('day').toDate()
            : formData.rangeDate.end;

        conditions.push({
            range: {
                fields: ["createdOn"],
                GTE: startUTC,
                LTE: endUTC
            }
        })
    }

    if (formData?.coaches && formData?.coaches.length) {
        conditions.push({
            fields: ["coachId"],
            terms: [...formData?.coaches.map(coach => coach.id)],
            exact: true
        })
    }

    if (formData?.drivers && formData?.drivers.length) {
        conditions.push({
            fields: ["driverId"],
            terms: [...formData?.drivers.map(driver => driver.driverId)],
            exact: true
        })
    }

    if (formData?.status && formData?.status.length) {
        conditions.push({
            fields: ["status_label"],
            terms: [...formData?.status.map(driver => driver.id)],
            exact: true
        })
    }

    if (formData?.tags && formData?.tags.length) {
        conditions.push({
            fields: ["driver_tagIds"],
            terms: [...formData?.tags.map(tag => tag.id)],
            exact: true
        })
    }

    if (formData?.page) {
        conditions.push({
            page: formData?.page
        })
    } else {
        // Always include page 1 if not specified to ensure proper reset
        conditions.push({
            page: 1
        })
    }
    if (formData?.casesNumber && formData?.casesNumber.length) {
        conditions.push({
            fields: ["caseNumber"],
            terms: [...formData?.casesNumber],
            exact: true
        })
    }

    return conditions;
};


export default DriverPerformanceCasesView;
